export class Table {
  constructor({ headers = [], items = [], readonly }) {
    this.headers = headers
    this.headersHashMap = this.headers.reduce((acc, {value}, idx) => ({...acc, [value]: idx}), {})

    const computeFirstHeaders = headers.filter(h => h.computeFirst !== false)
    this.rows = items.map((item, idx) => {
      const row = new Row({ item, headers, table: this, line: idx })
     
      if (!readonly) {
        computeFirstHeaders.forEach(header => {
          const cell = row.getCellFromHeader(header.value)
          row.computeCell(cell)
        })
      }

      return row
    })
  }

  validate() {
    return this.rows.every(row => row.validate())
  }

  findRow(item) {
    return this.rows.find(row => row.item == item)
  }
}

export class Row {
  constructor({ item = null, headers = null, table, line = -1 }) {
    this.table = table
    this.line = line
    this.item = item
    if (headers && item) {
      this.cells = headers.map((header, idx) => {
        const value = item[header.value] != undefined
          ? item[header.value]
          : 'default' in header
            ? header.default
            : null

        const name = header.value
        const mask = (value) => {
          const prefix = header.prefix
            ? header.prefix + ' '
            : ''

          const maskedValue = header.type == 'float' || header.type == 'integer'
            ? value.toLocaleString('pt-br', { minimumFractionDigits: header.precisionText, maximumFractionDigits: header.precisionText })
            : value

          const suffix = header.suffix
            ? ' ' + header.suffix
            : ''

          return prefix + maskedValue + suffix
        }
        const set = ({value, formMaster}) => {
          if (header.set) {
            const item = this.getItem()
            return header.set({ value, item, formMaster })
          } else {
            return value
          }
        }
        const validation = header.validation
        return new Cell({ value, name, set, mask, row: this, header, validation, x: idx, y: line })
      })

    } else if (item) {
      /**
       * @TODO
       */
      this.cells = Object.entries(item)
        .reduce((acc, [key, value]) => [...acc, new Cell({ value, name: key, row: this })], [])
    }
    
    // gambiarra para fazer os filtros e busca funcionarem (identificação da chamada do filtro por coluna)
    headers.forEach(h => {
      this[h.value] = h.value
    })
  }

  validate() {
    const validated = this.cells.every(cell => cell.validate())
    this.validated = validated
    return validated
  }

  validated() {
    return this.cells.every(cell => cell.validated)
  }

  getCellFromHeader(header) {
    return this.cells[this.table.headersHashMap[header]]
  }

  process({ cell, value, formMaster }) {

    cell.setValue({value, formMaster})

    if (!cell.validated)
      return

    const header = cell.header

    // Os headers/atributos a serem usados como busca não devem incluir o próprio elemento que está sendo alterado.
    const headers = this.table.headers.filter(h => h.value != header.value)

    // Array com os dependentes diretos do header, elimina a possibilidade do elemento ser dependente *direto* dele mesmo devido ao filter.
    const directDependentAttributes = this.getDependentAttributes(header, headers)

    // Headers a serem computados respeitando a ordem de precedência da menor posição do array pra maior.
    const dependencyChain = this.getDependencyChain(directDependentAttributes, headers)

    // for of para respeitar a ordem do array `dependencyChain`, que é a ordem de prioridade.
    for (const dependentHeaders of dependencyChain) {
      dependentHeaders.forEach(header => {
        const cell = this.getCellFromHeader(header.value)
        this.computeCell(cell)
      })
    }
  }

  computeCell(cell) {
    if (!cell.header || !cell.header.dependents)
      return

    const item = this.getDependentsItem(cell)
    
    
    const computedValue = cell.header.computed({ item, value: cell.value })
    cell.setValue({value: computedValue})
  }

  getDependentsItem(cell) {
    return cell.header.dependents.reduce((acc, dependent) => {
      const cell = this.getCellFromHeader(dependent)
      return { ...acc, [dependent]: cell.value }
    }, {})
  }

  /**
   * @TODO Considerar o uso do atributo item em vez de calcular o item com base nas celulas
   */
  getItem() {
    return this.cells.reduce((acc, cell) => {
      return { ...acc, [cell.header.value]: cell.value }
    }, {})
  }

  /**
   * Retorna um array de headers que dependem *diretamente* do `header` passado no primeiro parâmetro
   */
  getDependentAttributes (header, headers) {
    return headers.filter(h => h.dependents && h.dependents.includes(header.value))
  }

  /**
   * Retorna uma cadeia de headers que são dependentes dos headers passados no primeiro argumento.
   * A ordem dos elementos no array indicam o grau de dependência dos headers.
   */
  getDependencyChain(dependents, headers, dependencyChain = []) {
    if (dependents.length == 0)
      return dependencyChain

    const filteredHeaders = headers.filter(h => !dependents.includes(h))
    const newDependents = dependents
      .map(h => this.getDependentAttributes(h, filteredHeaders))
      .reduce((acc,arr) => [...acc, ...arr.filter(el => !acc.includes(el))], [])

    return this.getDependencyChain(newDependents, filteredHeaders, [...dependencyChain, dependents])
  }
}

export class Cell {
  constructor({ value = null, validation = [], set = value => value, mask = value => `${value}`, name, row, header, x = -1, y = -1 }) {
    this.validation = validation
    this.validated = true
    this.x = x
    this.y = y
    this.mask = mask
    this.set = set
    this.name = name
    this.row = row
    this.header = header
    this.errorMessages = []

    this.setValue({value, validate: false, set: false, verbose: false })
  }

  setValue({value, validate = true, set = true, mask = true, verbose = true, formMaster }) {

    if (value == null) {
      if (verbose)
        console.warn('"value" is null')

      this.text = ""
      this.value = value

      if (this.header.required) {
        this.validated = false
        this.errorMessages.push('Campo obrigatório')
      }
      return
    }

    if (this.header.type == 'integer') {
      value = parseInt(value)
      if (isNaN(value)) {
        if (verbose) {
          console.warn('"value" is NaN')
        }
        return
      }
    } else if (this.header.type == 'float') {
      value = parseFloat(value)
      if (isNaN(value)) {
        if (verbose) {
          console.warn('"value" is NaN')
        }
        return
      }
      value = this.fixPrecision(value, this.header.precisionValue)
    }

    this.value = set ? this.set({value, formMaster}) : value
    this.text = mask ? this.mask(this.value) : `${this.value}`
    this.validated = validate ? this.validate() : true

    // altera no item original somente se estiver validado
    if (this.validated)
      this.row.item[this.name] = this.value
  }

  validate() {
    this.errorMessages.splice(0)
    const item = this.row.getItem()
    return this.validation.every(f => {
      const validated = f(item)
      if (typeof validated == 'string') {
        this.errorMessages.push(validated)
        return false
      } else {
        return validated
      }
    })
  }

  fixPrecision(value, precision = 4) {
    if (!Number.isFinite(value))
      return NaN

    let fixed = value
    if (!Number.isInteger(fixed)) {
      let [integerPart, mantissa] = fixed.toString().split('.')
      
      if (mantissa && mantissa.length > precision) {
        const balanceDigit = parseInt(mantissa[precision])
        if (balanceDigit >= 5) {
          const fix = (parseInt(`${integerPart}${mantissa.slice(0, precision)}`) + 1).toString()
          
          integerPart = fix.slice(0, fix.length - precision)
          mantissa = fix.slice(-precision)
        } else {
          mantissa = mantissa.slice(0, precision)
        }
        fixed = parseFloat(integerPart + '.' + mantissa)
      }
    }

    return fixed
  }
}

export default { Table, Row, Cell }